import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Icon } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { UpdateTransactions } from "../../../../../../graphql/conciliacion";
import { importAlertState } from "../../../../../../redux/actions/alert";
import { importUpdateTransactions } from "../../../../../../redux/actions/conciliacion";
import CustomModal from "../../../../../configuracion/components/customModal";
import EOAFCategoryDropdown from "../../../../../../containers/EOAFCategoryDropdown";
import { GetObligationEOAFAssignments } from "../../../../../../graphql/eoaf";
import { formatMoneyWithCurrency } from "../../../../../../utils/formatter";

function resolveEoafKeyFromType(type, assignmentsList) {
  if (!type || !Array.isArray(assignmentsList) || assignmentsList.length === 0) return "";
  const doc = assignmentsList.find((d) => d?.obligationId === type);
  if (!doc?.eoafArea?.length) return "";
  const sorted = [...doc.eoafArea].sort((a, b) => {
    const pA = parseInt(a?.periodSince || "0", 10) || 0;
    const pB = parseInt(b?.periodSince || "0", 10) || 0;
    return pB - pA;
  });
  const latest = sorted[0];
  return latest?.creditCategoryKey || latest?.debitCategoryKey || "";
}

/**
 * RevisarMatchModal - Modal to edit EOAF assignment for a match (Categorizar Match).
 * Used from conciliación (MatchRow lupa) and from EOAF sidebar (Editar in 3-dots menu).
 */
const RevisarMatchModal = ({
  open,
  onClose,
  editData,
  match,
  companyId,
  onSuccess,
  title: titleProp,
}) => {
  const { t } = useTranslation("documents");
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth);
  const { executeMutation: updateTransactions } = UpdateTransactions();
  const { data: assignmentsData } = GetObligationEOAFAssignments({ companyId: companyId || "" });
  const assignmentsList = Array.isArray(assignmentsData) ? assignmentsData : [];

  const eoafForModal = match?.eoaf;
  // Monto del match en CLP (moneda local). Para casos USD/EUR→CLP el backend persiste
  // localValue con la equivalencia en pesos; ese es el valor que se debe usar para EOAF.
  const matchAmountForModal =
    match?.localValue ??
    match?.localAmount ??
    match?.matchedAmount ??
    match?.amount ??
    editData?.amountCLP ??
    editData?.amount ??
    0;

  // Rescalado de filas EOAF heredadas: si fueron guardadas en la moneda original
  // (suma == match.amount en moneda extranjera) y existe localValue distinto, se
  // re-expresan en CLP usando la razón localValue/amount para que cuadren con el total.
  const eoafScaleFactor = useMemo(() => {
    const local = Number(match?.localValue);
    const original = Number(match?.amount);
    if (!Number.isFinite(local) || !Number.isFinite(original)) return 1;
    if (local <= 0 || original <= 0) return 1;
    if (Math.abs(local - original) < 0.01) return 1;
    const eoafSum = (eoafForModal || []).reduce(
      (acc, e) => acc + (Number(e?.amount) || 0),
      0,
    );
    if (eoafSum <= 0) return 1;
    if (Math.abs(eoafSum - original) < 0.01) return local / original;
    return 1;
  }, [match?.localValue, match?.amount, eoafForModal]);

  const [revisarMatchRows, setRevisarMatchRows] = useState([]);
  const [revisarMatchValidationError, setRevisarMatchValidationError] = useState(null);
  const [isSavingRevisarMatch, setIsSavingRevisarMatch] = useState(false);

  useEffect(() => {
    if (!open || !editData || match == null) return;
    const matchAmount = matchAmountForModal;

    let initial;
    if (eoafForModal && eoafForModal.length > 0) {
      const isScaling = Math.abs(eoafScaleFactor - 1) > 1e-9;
      const scaled = eoafForModal.map((e) => (Number(e?.amount) || 0) * eoafScaleFactor);
      const amounts = isScaling
        ? (() => {
            const rounded = scaled.map((v) => Math.round(v));
            // Residual de redondeo se acumula en la última fila para que la suma cuadre con matchAmount.
            const sum = rounded.reduce((acc, n) => acc + n, 0);
            const diff = Math.round(matchAmount) - sum;
            if (rounded.length > 0 && diff !== 0) {
              rounded[rounded.length - 1] += diff;
            }
            return rounded;
          })()
        : scaled;

      initial = eoafForModal.map((e, idx) => {
        const areaKey =
          (e.key && String(e.key).trim()) || resolveEoafKeyFromType(e.type, assignmentsList) || "";
        const value = amounts[idx];
        return {
          amount: value,
          amountInput: String(value),
          areaKey,
          areaLabel: e.label ?? "",
          areaType: e.type ?? "",
        };
      });
    } else {
      initial = [
        { amount: matchAmount, amountInput: String(matchAmount), areaKey: "", areaLabel: "", areaType: "" },
      ];
    }
    setRevisarMatchRows(initial);
    setRevisarMatchValidationError(null);
  }, [open, editData, match, matchAmountForModal, eoafForModal, assignmentsList, eoafScaleFactor]);

  const addRevisarMatchRow = useCallback(() => {
    const txAmount = matchAmountForModal;
    setRevisarMatchRows((prev) => {
      const assignedTotal = prev.reduce((sum, r) => sum + (r.amount || 0), 0);
      const difference = txAmount - assignedTotal;
      const newAmount = Math.max(0, difference);
      const displayAmount =
        newAmount % 1 === 0 ? String(newAmount) : String(Number(newAmount.toFixed(2)));
      return [...prev, { amount: newAmount, amountInput: displayAmount, areaKey: "", areaLabel: "", areaType: "" }];
    });
  }, [matchAmountForModal]);

  const updateRevisarMatchRow = useCallback((index, field, value) => {
    setRevisarMatchValidationError(null);
    setRevisarMatchRows((prev) => {
      const next = [...prev];
      const row = { ...next[index] };
      if (field === "amountInput") {
        row.amountInput = value;
        const num = parseFloat(value.replace(/\s/g, "").replace(",", ".")) || 0;
        row.amount = num;
      } else if (field === "areaKey") {
        row.areaKey = value ?? "";
      } else if (field === "area") {
        row.areaKey = value?.areaKey ?? row.areaKey ?? "";
        row.areaLabel = value?.areaLabel ?? row.areaLabel ?? "";
        row.areaType = value?.areaType ?? row.areaType ?? "";
      }
      next[index] = row;
      return next;
    });
  }, []);

  const removeRevisarMatchRow = useCallback((index) => {
    setRevisarMatchRows((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );
  }, []);

  const handleGuardarRevisarMatch = useCallback(async () => {
    setRevisarMatchValidationError(null);
    const hasRowWithAmountButNoArea = revisarMatchRows.some(
      (r) => (Number(r.amount) || 0) > 0 && !(r.areaKey && String(r.areaKey).trim())
    );
    if (hasRowWithAmountButNoArea) {
      setRevisarMatchValidationError(t("reconcile.revisarMatch.noCategoryAssigned"));
      return;
    }
    if (!editData?.matchs || !companyId) {
      dispatch(importAlertState({ type: "error", message: t("reconcile.revisarMatch.missingTransactionData") }));
      return;
    }
    const docIdStr = String(match?.id ?? match?.docId ?? match?._id ?? "");
    if (!docIdStr) {
      dispatch(importAlertState({ type: "error", message: t("reconcile.revisarMatch.matchNotIdentified") }));
      return;
    }
    const newEoaf = revisarMatchRows
      .filter((r) => r.areaKey || (r.amount != null && r.amount !== 0))
      .map((r) => ({
        type: r.areaType ?? "",
        label: r.areaLabel ?? "",
        key: r.areaKey || "",
        amount: Number(r.amount) || 0,
      }));
    const eoafUpdatedBy = {
      userId: currentUser?.uid ?? currentUser?.id ?? "",
      email: currentUser?.email ?? "",
      date: new Date().toISOString(),
    };
    const existingMatchs = editData.matchs || [];
    const hasMatch = existingMatchs.some((m) => String(m?.docId) === docIdStr);
    const updatedMatchs = hasMatch
      ? existingMatchs.map((m) =>
          String(m?.docId) === docIdStr ? { ...m, eoaf: newEoaf, eoafUpdatedBy } : m
        )
      : [...existingMatchs, { docId: docIdStr, amount: matchAmountForModal, eoaf: newEoaf, eoafUpdatedBy }];
    setIsSavingRevisarMatch(true);
    try {
      const result = await updateTransactions({
        companyId,
        input: {
          documents: [
            {
              id: editData.id || editData._id,
              fields: [{ key: "matchs", value: updatedMatchs }],
            },
          ],
        },
      });
      if (result?.error) {
        dispatch(importAlertState({ type: "error", message: t("reconcile.revisarMatch.saveFailed") }));
        return;
      }
      dispatch(importAlertState({ type: "success", message: t("reconcile.revisarMatch.saveSuccess") }));
      dispatch(importUpdateTransactions(true));
      onClose?.();
      onSuccess?.();
    } catch (e) {
      dispatch(importAlertState({ type: "error", message: t("reconcile.revisarMatch.saveFailed") }));
    } finally {
      setIsSavingRevisarMatch(false);
    }
  }, [editData, match, companyId, revisarMatchRows, dispatch, updateTransactions, currentUser, onClose, onSuccess, matchAmountForModal, t]);

  const canSaveRevisarMatch = useMemo(() => {
    const txAmount = matchAmountForModal;
    const assignedTotal = revisarMatchRows.reduce((sum, r) => sum + (r.amount || 0), 0);
    const isBalanced = Math.abs(txAmount - assignedTotal) < 0.01;
    const hasRowWithAmountButNoArea = revisarMatchRows.some(
      (r) => (Number(r.amount) || 0) > 0 && !(r.areaKey && String(r.areaKey).trim())
    );
    return isBalanced && !hasRowWithAmountButNoArea;
  }, [revisarMatchRows, matchAmountForModal]);

  const defaultTitle = useMemo(() => {
    if (titleProp) return titleProp;
    if (!editData) return t("reconcile.popups.categorizeMatch");
    const typeLabel =
      editData.type === "RECIEVED" || editData.type === "RECEIVED"
        ? t("reconcile.credit")
        : t("reconcile.debit");
    const titleAmount = matchAmountForModal || editData.amountCLP || editData.amount;
    const amountFormatted = formatMoneyWithCurrency(titleAmount, "CLP", true, true);
    const desc =
      editData.description ||
      (editData.type === "RECIEVED" ? editData.issuerName : editData.receiverName) ||
      t("reconcile.revisarMatch.movementFallback");
    return t("reconcile.revisarMatch.titlePattern", {
      type: typeLabel,
      amount: amountFormatted,
      description: desc,
    });
  }, [titleProp, editData, matchAmountForModal, t]);

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={defaultTitle}
      content={
        <div className="match-row-revisar-match-modal-content">
          {(() => {
            const txAmount = matchAmountForModal;
            const assignedTotal = revisarMatchRows.reduce((sum, r) => sum + (r.amount || 0), 0);
            const difference = Math.abs(txAmount - assignedTotal);
            const isBalanced = difference < 0.01;
            const hasRowWithAmountButNoArea = revisarMatchRows.some(
              (r) => (Number(r.amount) || 0) > 0 && !(r.areaKey && String(r.areaKey).trim())
            );
            const showAsignarAreaMessage = revisarMatchValidationError || hasRowWithAmountButNoArea;
            const isTotalsUnbalanced = !isBalanced || showAsignarAreaMessage;
            return (
              <>
                <div
                  className={`match-row-revisar-match-totals journal-entry-totals ${isTotalsUnbalanced ? "journal-entry-totals--unbalanced" : "journal-entry-totals--balanced"}`}
                >
                  <p className="journal-entry-totals-text">
                    {t("reconcile.revisarMatch.matchAmount")}: {formatMoneyWithCurrency(txAmount, "CLP")} |{" "}
                    {t("reconcile.revisarMatch.assigned")}: {formatMoneyWithCurrency(assignedTotal, "CLP")}
                    {!isBalanced &&
                      ` | ${t("reconcile.revisarMatch.difference")}: ${formatMoneyWithCurrency(difference, "CLP")}`}
                    {showAsignarAreaMessage && (
                      <span style={{ color: "#c62828", marginLeft: "8px" }}>
                        | {t("reconcile.revisarMatch.mustAssignCategory")}
                      </span>
                    )}
                  </p>
                  <Icon
                    name={isTotalsUnbalanced ? "exclamation circle" : "check circle"}
                    className="journal-entry-totals-icon"
                  />
                </div>
                <div className="journal-entry-divider" />
                <div className="journal-entry-rows-container">
                  {revisarMatchRows.map((row, index) => (
                    <div key={index} className="journal-entry-row match-row-revisar-match-row">
                      {revisarMatchRows.length > 1 && (
                        <button
                          type="button"
                          className="journal-entry-row-remove-x"
                          onClick={() => removeRevisarMatchRow(index)}
                          title={t("reconcile.revisarMatch.removeLine")}
                          aria-label={t("reconcile.revisarMatch.removeLine")}
                        >
                          ×
                        </button>
                      )}
                      <div className="journal-entry-row-account">
                        <label className="journal-entry-amount-label">{t("reconcile.revisarMatch.eoafCategory")}</label>
                        <EOAFCategoryDropdown
                          companyId={companyId}
                          value={row.areaKey}
                          onChange={(e, data) => {
                            const areaKey = data?.value ?? "";
                            const areaLabel = data?.label ?? "";
                            const areaType =
                              (data?.node && typeof data.node === "object" && data.node.type) != null
                                ? data.node.type
                                : areaLabel
                                  ? String(areaLabel).toLowerCase().replace(/\s+/g, "")
                                  : "";
                            updateRevisarMatchRow(index, "area", {
                              areaKey,
                              areaLabel,
                              areaType,
                            });
                          }}
                          placeholder={t("reconcile.revisarMatch.selectCategory")}
                          className="match-row-revisar-match-dropdown"
                        />
                      </div>
                      <div className="journal-entry-row-amounts">
                        <div className="journal-entry-amount-group">
                          <label className="journal-entry-amount-label">{t("reconcile.revisarMatch.amount")}</label>
                          <input
                            type="text"
                            className="journal-entry-amount-input"
                            value={row.amountInput}
                            onChange={(e) =>
                              updateRevisarMatchRow(index, "amountInput", e.target.value)
                            }
                            placeholder="0"
                            inputMode="decimal"
                            aria-label={t("reconcile.revisarMatch.amount")}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="journal-entry-add-button"
                    onClick={addRevisarMatchRow}
                  >
                    <Icon name="plus" />
                    {t("reconcile.revisarMatch.addCategory")}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      }
      cancelarLabel={t("reconcile.revisarMatch.close")}
      confirmLabel={t("reconcile.revisarMatch.save")}
      onConfirm={handleGuardarRevisarMatch}
      confirmDisabled={isSavingRevisarMatch || !canSaveRevisarMatch}
      isLoading={isSavingRevisarMatch}
      size="large"
      classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold"
      classNameModal="match-row-revisar-match-modal"
      classNameContent="match-row-revisar-match-modal-content-wrapper"
      dimmer={{ style: { zIndex: 9999, backgroundColor: "rgba(0,0,0,0.5)" } }}
      modalStyle={{
        borderRadius: "16px",
        maxWidth: "95%",
        minWidth: "880px",
        margin: "0 auto",
      }}
    />
  );
};

export default RevisarMatchModal;
